import type { Language } from '@/shared/utils/i18n';

/**
 * CV / 简历数据（真实信息，来源：README.md / .cursor/rules/information.mdc）。
 * 更新简历只需要改这个文件，不需要动任何组件。
 */

export interface Localized {
  en: string;
  zh: string;
}

export const pick = (text: Localized, lang: Language): string => text[lang];

export interface EducationEntry {
  degree: Localized;
  field: Localized;
  institution: Localized;
  year: Localized;
  description: Localized;
}

export interface ExperienceEntry {
  title: Localized;
  company: Localized;
  period: Localized;
  location?: Localized;
  description: Localized[];
}

export interface ProjectEntry {
  title: Localized;
  period: Localized;
  description: Localized[];
}

export interface VolunteerEntry {
  title: Localized;
  organization: Localized;
  period?: Localized;
  description: Localized[];
}

export interface SkillGroup {
  category: Localized;
  items: Localized[];
}

export const education: EducationEntry[] = [
  {
    degree: { en: 'B.A.', zh: '文学学士' },
    field: { en: 'History, Politics and Economics', zh: '历史、政治与经济学' },
    institution: { en: 'University College London', zh: '伦敦大学学院' },
    year: { en: '2023 – 2026', zh: '2023 – 2026' },
    description: {
      en: 'Interdisciplinary study across history, politics and economics, with interests in cognitive and behavioural science, philosophy, and the interpretation of human decision-making in social systems.',
      zh: '跨历史、政治与经济学的交叉学科学习，关注认知与行为科学、哲学，以及社会系统中人类决策的理解与诠释。'
    }
  }
];

export const experience: ExperienceEntry[] = [
  {
    title: { en: 'Student Ambassador', zh: '学生大使' },
    company: { en: 'University College London', zh: '伦敦大学学院' },
    period: { en: 'Nov 2024 – Jun 2025', zh: '2024年11月 – 2025年6月' },
    location: { en: 'London, UK · Part-time', zh: '英国伦敦 · 兼职' },
    description: [
      {
        en: 'Student Ambassador to the Vice-President External Engagement (VPEE) teams.',
        zh: '担任副校长对外事务（VPEE）团队的学生大使。'
      }
    ]
  },
  {
    title: { en: 'Full Stack Developer', zh: '全栈开发' },
    company: { en: 'UCL API', zh: 'UCL API' },
    period: { en: 'Mar 2025', zh: '2025年3月' },
    location: { en: 'London, UK', zh: '英国伦敦' },
    description: [
      {
        en: "Developed dashUCL, a native iOS application integrating with the UCL API.",
        zh: '开发了与 UCL API 集成的原生 iOS 应用 dashUCL。'
      }
    ]
  },
  {
    title: {
      en: 'Investment Analyst Intern — Life Science & IVD',
      zh: '投资分析实习生 — 生命科学与 IVD'
    },
    company: { en: 'Sunshine Riverhead Capital', zh: 'Sunshine Riverhead Capital' },
    period: { en: 'Jun – Aug 2024', zh: '2024年6月 – 8月' },
    location: { en: 'Shanghai, China · Internship', zh: '中国上海 · 实习' },
    description: [
      {
        en: 'Conducted in-depth industry analysis on cardiopulmonary intervention devices, surgical robotics, and CDMO for IVD utilities across 7 deals.',
        zh: '针对 7 个项目开展心肺介入器械、手术机器人及 IVD CDMO 的深度行业分析。'
      },
      {
        en: 'Supported due diligence by engaging in strategic discussions with senior executives and preparing investment memoranda.',
        zh: '参与与高管的战略讨论并撰写投资备忘录，支持尽职调查流程。'
      },
      {
        en: 'Developed financial models and conducted market assessments.',
        zh: '搭建财务模型并完成市场评估。'
      }
    ]
  },
  {
    title: { en: 'Consultant', zh: '咨询顾问' },
    company: { en: 'Cambridge Consulting Network (CCN)', zh: 'Cambridge Consulting Network (CCN)' },
    period: { en: 'Aug – Oct 2024', zh: '2024年8月 – 10月' },
    location: { en: 'London, UK · Part-time', zh: '英国伦敦 · 兼职' },
    description: [
      {
        en: 'Provided strategic analysis and marketing strategies for a tech company expanding a software-and-hardware product for agricultural and educational aid in underdeveloped regions.',
        zh: '为一家软硬件结合、面向欠发达地区农业与教育援助的科技公司提供战略分析与营销策略。'
      },
      {
        en: 'Enhanced business feasibility evaluation with comprehensive data-driven market entry strategies.',
        zh: '以数据驱动的市场进入策略完善商业可行性评估。'
      },
      {
        en: 'Contributed to a 15% increase in projected market penetration through refined marketing and outreach plans.',
        zh: '通过优化营销与推广方案，助力预期市场渗透率提升 15%。'
      }
    ]
  },
  {
    title: { en: 'Business Operation & Content Creator', zh: '商业运营与内容创作' },
    company: { en: 'Xiaohongshu', zh: '小红书' },
    period: { en: 'Jun 2022 – Sep 2024', zh: '2022年6月 – 2024年9月' },
    description: [
      {
        en: 'Established and managed a supply-to-distribution network, coordinating cross-functional teams to a 30% increase in distribution reach; dynamic pricing lifted overall revenue by 25%.',
        zh: '搭建并管理供应-分销网络，协调跨职能团队使分销触达提升 30%；动态定价策略带动整体营收增长 25%。'
      },
      {
        en: 'Created 100+ pieces of content gaining over 400K exposure; cooperated with brands including Airbnb and 7artisans in marketing campaigns.',
        zh: '创作 100+ 条内容、累计曝光超 40 万；与 Airbnb、七工匠等品牌合作开展营销活动。'
      }
    ]
  },
  {
    title: { en: 'Asset & Wealth Management Challenge — Team Leader', zh: '资产与财富管理挑战赛 — 队长' },
    company: { en: 'J.P. Morgan', zh: '摩根大通' },
    period: { en: 'Feb – Mar 2024', zh: '2024年2月 – 3月' },
    location: { en: 'China', zh: '中国' },
    description: [
      {
        en: 'Built a comprehensive economic outlook from economic indicators and investment reports, focusing on U.S. inflation and Fed rate-cut expectations.',
        zh: '基于经济指标与投研报告构建宏观经济展望，聚焦美国通胀形势与美联储降息预期。'
      },
      {
        en: 'Performed industry research on the AI sector, refining the supply-chain structure and identifying potential investment targets.',
        zh: '对 AI 行业开展深度研究，梳理供应链结构并提出潜在投资标的。'
      },
      {
        en: 'Delivered investment recommendations through a detailed 12-page presentation with graphics-led storytelling.',
        zh: '以 12 页图表化演示交付完整投资建议。'
      }
    ]
  }
];

export const projects: ProjectEntry[] = [
  {
    title: { en: 'Microsoft AI Agent Hackathon — MockingJay', zh: '微软 AI Agent 黑客松 — MockingJay' },
    period: { en: 'Apr – May 2025', zh: '2025年4月 – 5月' },
    description: [
      {
        en: 'Building MockingJay, an LLM-interactive agent for quant strategy deployed on high-frequency trading bots, utilising the Hummingbot framework.',
        zh: '构建 MockingJay：部署于高频交易机器人、基于 Hummingbot 框架的 LLM 交互式量化策略智能体。'
      }
    ]
  },
  {
    title: {
      en: 'Height-Dependent Rotor Noise and Thrust in Urban Air Mobility',
      zh: '城市空中交通中随高度变化的旋翼噪声与推力研究'
    },
    period: { en: 'Apr 2025', zh: '2025年4月' },
    description: [
      {
        en: 'Acknowledged contributor for computational analysis in experimental research on eVTOL rotor noise, aeroacoustics, aerodynamics and vortex shedding.',
        zh: '作为致谢贡献者承担计算分析工作，研究涵盖 eVTOL 旋翼噪声、气动声学、空气动力学与涡脱落。'
      }
    ]
  },
  {
    title: { en: 'dashUCL — Navigate UCL with Brilliance', zh: 'dashUCL — 优雅地穿行 UCL' },
    period: { en: 'Mar 2025', zh: '2025年3月' },
    description: [
      {
        en: 'A native iOS app delivering essential information for UCL students with minimal interaction: timetable and reminders, real-time space availability, and a faculty directory.',
        zh: '原生 iOS 应用，用最少交互提供 UCL 学生最需要的信息：课表提醒、实时空间查询与院系通讯录。'
      }
    ]
  },
  {
    title: { en: 'Extended Project Qualification', zh: 'EPQ 研究项目' },
    period: { en: 'Sep 2021 – Jan 2022', zh: '2021年9月 – 2022年1月' },
    description: [
      {
        en: 'Researched how language environments influence political decision-making, focusing on linguistic structures and bilingual individuals’ shifts in policy perspectives across languages.',
        zh: '研究语言环境如何影响政治决策，聚焦语言结构以及双语者在不同语言下政策立场的偏移。'
      }
    ]
  },
  {
    title: { en: 'R.A. Butler Prize in Politics and International Studies', zh: 'R.A. Butler 政治与国际研究论文奖' },
    period: { en: 'Aug – Sep 2021', zh: '2021年8月 – 9月' },
    description: [
      {
        en: 'Authored a data-driven essay on why developing countries suffer more frequently from conflict, integrating statistical evidence with theoretical frameworks.',
        zh: '撰写数据驱动的论文，探讨发展中国家为何更频繁遭受冲突，将统计证据与理论框架相结合。'
      }
    ]
  }
];

export const volunteer: VolunteerEntry[] = [
  {
    title: { en: 'Marathon Track First-Aid Runner', zh: '马拉松赛道急救跑者' },
    organization: { en: 'First Respond®', zh: '第一反应®' },
    description: [
      {
        en: 'AHA-licensed first-aider participating in marathon track support with First Respond.',
        zh: '持 AHA 急救认证，随第一反应参与马拉松赛道保障。'
      }
    ]
  },
  {
    title: { en: 'Guide Dog Sponsor', zh: '导盲犬资助人' },
    organization: { en: 'The Guide Dogs for the Blind Association', zh: 'The Guide Dogs for the Blind Association' },
    period: { en: 'Apr 2025 – Present', zh: '2025年4月 – 至今' },
    description: [
      {
        en: 'Proudly sponsoring three puppy trainees — Henry, Norman and Clover — through their two-year guide dog training journey.',
        zh: '资助 Henry、Norman、Clover 三只幼犬学员完成为期两年的导盲犬训练。'
      }
    ]
  }
];

export const skills: SkillGroup[] = [
  {
    category: { en: 'Full-Stack Development', zh: '全栈开发' },
    items: [
      { en: 'React / TypeScript / Tailwind CSS', zh: 'React / TypeScript / Tailwind CSS' },
      { en: 'Node.js / PostgreSQL', zh: 'Node.js / PostgreSQL' },
      { en: 'Swift (iOS) / Electron', zh: 'Swift (iOS) / Electron' }
    ]
  },
  {
    category: { en: 'Robotics & Hardware', zh: '机器人与硬件' },
    items: [
      { en: 'STM32H743 / ESP32-S3 / RK3588', zh: 'STM32H743 / ESP32-S3 / RK3588' },
      { en: 'C/C++ / Python', zh: 'C/C++ / Python' },
      { en: 'GNSS / IMU / dTOF LiDAR integration', zh: 'GNSS / IMU / dTOF 激光雷达集成' }
    ]
  },
  {
    category: { en: 'Languages', zh: '语言' },
    items: [
      { en: 'English', zh: '英语' },
      { en: 'Chinese (Native)', zh: '中文（母语）' }
    ]
  }
];
